import React from 'react';
import { 
  Building2, 
  HardHat, 
  FileCheck, 
  ShieldCheck, 
  Grid, 
  Zap,
  LogOut,
  Sparkles
} from 'lucide-react';

export type PortalRole = 'foreman' | 'pto' | 'hr' | 'admin';

interface PortalHeaderProps {
  role: PortalRole;
  activeSubTab?: string;
  onSubTabChange?: (tab: string) => void;
  onLogout: () => void;
  serverStatus?: { status: string; db: string; latencyMs?: number } | null;
  alertsCount?: number;
}

export const PortalHeader: React.FC<PortalHeaderProps> = ({
  role,
  onLogout,
  serverStatus,
  alertsCount = 0
}) => {
  const roleConfig = {
    foreman: {
      title: 'Полевой PWA-портал Прораба',
      icon: HardHat,
      badge: 'Мобильный PWA',
      badgeStyle: 'bg-amber-50 text-amber-700 border-amber-200',
      user: 'Бригадир / Прораб (Иванов И.И.)',
      avatarBg: 'bg-amber-100 text-amber-800 border-amber-300'
    },
    pto: {
      title: 'Инженерный портал ПТО & АОСР',
      icon: FileCheck,
      badge: 'Акты & КС-6а',
      badgeStyle: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      user: 'Инженер ПТО (Сидоров А.В.)',
      avatarBg: 'bg-emerald-100 text-emerald-800 border-emerald-300'
    },
    hr: {
      title: 'Кадровый портал & Охрана Труда',
      icon: ShieldCheck,
      badge: alertsCount > 0 ? `${alertsCount} алертов` : 'ОТ & Допуски ЭБ',
      badgeStyle: alertsCount > 0 
        ? 'bg-rose-50 text-rose-700 border-rose-200 animate-pulse' 
        : 'bg-blue-50 text-blue-700 border-blue-200',
      user: 'Специалист по ОТ (Петрова Е.Н.)',
      avatarBg: 'bg-blue-100 text-blue-800 border-blue-300'
    },
    admin: {
      title: 'Панель Главного Инженера & 2D-Шахматка',
      icon: Grid,
      badge: 'Контроль Объекта',
      badgeStyle: 'bg-purple-50 text-purple-700 border-purple-200',
      user: 'Главный Инженер (Ковалев В.М.)',
      avatarBg: 'bg-purple-100 text-purple-800 border-purple-300'
    }
  };

  const config = roleConfig[role];
  const Icon = config.icon;

  return (
    <header className="bg-white border-b border-slate-200/80 sticky top-0 z-50 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Brand & Portal Info */}
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl text-white font-black shadow-md shadow-amber-500/20 flex items-center justify-center">
              <Zap className="w-5 h-5 fill-current" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight flex items-center gap-2">
                  <Icon className="w-5 h-5 text-slate-700" />
                  <span>{config.title}</span>
                </h1>
                <span className={`text-xs px-2.5 py-0.5 rounded-full border font-semibold ${config.badgeStyle}`}>
                  {config.badge}
                </span>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-slate-700 font-medium">ЖК «Северная Звезда»</span> (Блоки А/Б, 12 этажей, ГРЩ)
                <span className="text-slate-400">• ООО «ЭлектроМонтажСтрой»</span>
              </p>
            </div>
          </div>

          {/* User Badge, DB Status & Logout */}
          <div className="flex items-center space-x-3 text-xs self-end md:self-center">
            <div className="hidden lg:flex items-center space-x-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-slate-500">БД / Cloudflare:</span>
              <span className="font-semibold text-emerald-700">
                {serverStatus?.status === 'ok' ? 'Онлайн' : 'Подключение...'}
              </span>
            </div>

            {/* Current User Card */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200">
              <div className={`w-7 h-7 rounded-lg font-bold text-xs flex items-center justify-center border ${config.avatarBg}`}>
                {config.user.charAt(0)}
              </div>
              <span className="font-medium text-slate-800 text-xs hidden sm:inline">
                {config.user}
              </span>
            </div>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-3 py-1.5 rounded-xl font-semibold transition active:scale-95 shadow-sm"
              title="Выйти в холл входа"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Выход / Сменить роль</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default PortalHeader;
